import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ChevronRight,
  BarChart3,
  Layers,
  Target,
  FileText,
  Menu,
  Settings
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { usePermissions } from '@/hooks/usePermissions';
import { useUserScopes } from '@/hooks/useUserScopes';
import { Permission } from '@/utils/permissions';

interface SidebarItem {
  id: string;
  label: string;
  href?: string;
  icon?: React.ComponentType<{ className?: string }>;
  children?: SidebarItem[];
  badge?: string | number;
  permission?: Permission;
  anyOf?: Permission[];
  requiresResourceAccess?: boolean;
  requiresTeamsAccess?: boolean;
}

interface SidebarProps {
  isCollapsed?: boolean;
  onToggle?: () => void;
  className?: string;
}

const Sidebar: React.FC<SidebarProps> = ({
  isCollapsed = false,
  onToggle,
  className
}) => {
  const location = useLocation();
  const { can, canAny } = usePermissions();
  const { canAccessResourceManagement, canAccessTeams } = useUserScopes();
  const [expandedItems, setExpandedItems] = useState<string[]>(['portfolio', 'delivery', 'administration']);

  const sidebarItems: SidebarItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      href: '/dashboard',
      icon: BarChart3
    },
    {
      id: 'portfolio',
      label: 'Portfolio',
      icon: Layers,
      anyOf: [Permission.VIEW_INVESTIBLE_STRUCTURES, Permission.VIEW_PROGRAMS, Permission.VIEW_TEAMS, Permission.VIEW_EPICS, Permission.VIEW_FEATURES, Permission.VIEW_PLANNING_PERIODS],
      children: [
        {
          id: 'planning-periods',
          label: 'Planning Periods',
          href: '/planning-periods',
          permission: Permission.VIEW_PLANNING_PERIODS
        },
        {
          id: 'investible-structures',
          label: 'Investible Structures',
          href: '/investible-structures',
          permission: Permission.VIEW_INVESTIBLE_STRUCTURES
        },
        {
          id: 'divisions',
          label: 'Divisions',
          href: '/divisions',
          permission: Permission.VIEW_PROGRAMS
        },
        {
          id: 'programs',
          label: 'Programs',
          href: '/programs',
          permission: Permission.VIEW_PROGRAMS
        },
        {
          id: 'teams',
          label: 'Teams',
          href: '/teams',
          permission: Permission.VIEW_TEAMS,
          requiresTeamsAccess: true
        },
        {
          id: 'resources',
          label: 'Resources',
          href: '/resources',
          permission: Permission.VIEW_PROGRAMS,
          requiresResourceAccess: true
        },
        {
          id: 'resource-assignments',
          label: 'Resource Assignments',
          href: '/resource-assignments',
          permission: Permission.VIEW_PROGRAMS,
          requiresResourceAccess: true
        },
        {
          id: 'resource-intake-requests',
          label: 'Resource Requests',
          href: '/resource-intake-requests',
          permission: Permission.VIEW_PROGRAMS,
          requiresResourceAccess: true
        },
        {
          id: 'epics',
          label: 'EPICs',
          href: '/epics',
          permission: Permission.VIEW_EPICS
        },
        {
          id: 'features',
          label: 'Features',
          href: '/features',
          permission: Permission.VIEW_FEATURES
        },
        {
          id: 'okrs',
          label: 'OKRs',
          href: '/okrs',
          permission: Permission.VIEW_EPICS
        },
        {
          id: 'scenarios',
          label: 'Scenarios',
          href: '/scenarios',
          permission: Permission.VIEW_SCENARIOS
        },
        {
          id: 'dependencies',
          label: 'Dependencies',
          href: '/dependencies',
          permission: Permission.VIEW_DEPENDENCIES
        },
        {
          id: 'consolidated-allocations',
          label: 'Consolidated Allocations',
          href: '/allocations/consolidated',
          permission: Permission.VIEW_EPICS
        }
      ]
    },
    {
      id: 'delivery',
      label: 'Delivery',
      icon: Target,
      anyOf: [Permission.VIEW_CAPACITY, Permission.VIEW_REPORTS, Permission.VIEW_EPICS],
      children: [
        {
          id: 'program-capacity',
          label: 'Program Capacity',
          href: '/capacity/programs',
          permission: Permission.VIEW_CAPACITY
        },
        {
          id: 'operational-cost',
          label: 'Operational Cost',
          href: '/finance/operational-cost',
          permission: Permission.VIEW_CAPACITY
        },
        {
          id: 'cost-dashboard',
          label: 'Cost Dashboard',
          href: '/costs/dashboard',
          permission: Permission.VIEW_EXECUTIVE_DASHBOARD
        },
        {
          id: 'division-budgets',
          label: 'Division Budgets',
          href: '/finance/division-budgets',
          permission: Permission.VIEW_EXECUTIVE_DASHBOARD
        },
        {
          id: 'timeline',
          label: 'Timeline',
          href: '/timeline',
          permission: Permission.VIEW_EPICS
        },
        {
          id: 'reports',
          label: 'Reports',
          icon: FileText,
          permission: Permission.VIEW_REPORTS,
          children: [
            {
              id: 'executive-dashboard',
              label: 'Executive Dashboard',
              href: '/reports/executive',
              permission: Permission.VIEW_EXECUTIVE_DASHBOARD
            },
            {
              id: 'is-owner-dashboard',
              label: 'IS Owner Dashboard',
              href: '/reports/is-owner',
              permission: Permission.VIEW_IS_OWNER_DASHBOARD
            },
            {
              id: 'program-lead-dashboard',
              label: 'Program Lead Dashboard',
              href: '/reports/program-lead',
              permission: Permission.VIEW_PROGRAM_LEAD_DASHBOARD
            },
            {
              id: 'capacity-reports',
              label: 'Capacity Reports',
              href: '/reports/capacity',
              permission: Permission.VIEW_CAPACITY
            }
          ]
        }
      ]
    },
    {
      id: 'administration',
      label: 'Administration',
      icon: Settings,
      anyOf: [Permission.MANAGE_USERS, Permission.VIEW_AUDIT_LOGS, Permission.VIEW_RATE_CARDS],
      children: [
        {
          id: 'users',
          label: 'User Management',
          href: '/users',
          permission: Permission.MANAGE_USERS
        },
        {
          id: 'rate-cards',
          label: 'Rate Cards',
          href: '/rate-cards',
          permission: Permission.VIEW_RATE_CARDS
        }
      ]
    }
  ];

  const isActiveRoute = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(href);
  };

  const hasActiveChild = (item: SidebarItem): boolean => {
    if (!item.children) return false;
    return item.children
      .filter(child => hasPermissionForItem(child))
      .some(child => 
        child.href ? isActiveRoute(child.href) : hasActiveChild(child)
      );
  };

  const hasPermissionForItem = (item: SidebarItem): boolean => {
    // Check resource access requirements first
    if (item.requiresResourceAccess && !canAccessResourceManagement()) {
      return false;
    }

    // Check teams access requirements (IS_OWNER cannot see Teams)
    if (item.requiresTeamsAccess && !canAccessTeams()) {
      return false;
    }

    if (item.permission) {
      return can(item.permission);
    }
    if (item.anyOf) {
      return canAny(item.anyOf);
    }
    return true; // No permission specified, allow access
  };

  const getVisibleChildren = (item: SidebarItem): SidebarItem[] => {
    if (!item.children) return [];
    return item.children.filter(child => hasPermissionForItem(child));
  };

  const toggleExpanded = (itemId: string) => {
    setExpandedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const renderSidebarItem = (item: SidebarItem, level = 0) => {
    // Check permissions first
    if (!hasPermissionForItem(item)) {
      return null;
    }

    const isExpanded = expandedItems.includes(item.id);
    const isActive = item.href ? isActiveRoute(item.href) : hasActiveChild(item);
    const visibleChildren = getVisibleChildren(item);
    const hasChildren = visibleChildren.length > 0;

    const itemBaseClass = cn(
      'group flex items-center gap-3 px-3 py-2.5 text-[13px] font-medium rounded-xl transition-all duration-200',
      level > 0 && !isCollapsed && 'ml-3'
    );

    const activeClass = isActive
      ? 'bg-[var(--accent-blue-soft)] border-l-3 border-l-[var(--accent-blue)]'
      : 'hover:bg-surface-muted/50';

    return (
      <div key={item.id}>
        {item.href ? (
          <Link
            to={item.href}
            className={cn(itemBaseClass, activeClass)}
            style={{
              color: isActive ? 'var(--accent-blue)' : 'var(--glass-text-muted)',
            }}
            title={item.label}
          >
            {item.icon && level === 0 && (
              <item.icon
                className={cn('w-5 h-5 flex-shrink-0', isActive ? 'opacity-100' : 'opacity-70')}
              />
            )}
            {level > 0 && !isCollapsed && (
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{
                  backgroundColor: isActive ? 'var(--accent-blue)' : 'var(--text-subtle)',
                }}
              />
            )}
            {!isCollapsed && (
              <>
                <span className="flex-1">{item.label}</span>
                {item.badge && (
                  <span
                    className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                    style={{
                      background: 'linear-gradient(135deg, var(--accent-blue), var(--accent-violet))',
                      color: 'white',
                    }}
                  >
                    {item.badge}
                  </span>
                )}
              </>
            )}
          </Link>
        ) : (
          <button
            onClick={() => toggleExpanded(item.id)}
            className={cn(itemBaseClass, activeClass, 'w-full')}
            style={{
              color: isActive ? 'var(--glass-text-primary)' : 'var(--glass-text-muted)',
            }}
            title={item.label}
          >
            {item.icon && level === 0 && (
              <item.icon
                className={cn('w-5 h-5 flex-shrink-0', isActive ? 'opacity-100' : 'opacity-70')}
              />
            )}
            {!isCollapsed && (
              <>
                <span className="flex-1 text-left">{item.label}</span>
                {item.badge && (
                  <span
                    className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                    style={{
                      background: 'linear-gradient(135deg, var(--accent-blue), var(--accent-violet))',
                      color: 'white',
                    }}
                  >
                    {item.badge}
                  </span>
                )}
                {hasChildren && (
                  <ChevronRight
                    className={cn(
                      'w-4 h-4 transition-transform duration-200',
                      isExpanded && 'rotate-90'
                    )}
                    style={{ color: 'var(--glass-text-subtle)' }}
                  />
                )}
              </>
            )}
          </button>
        )}

        {/* Children */}
        {hasChildren && isExpanded && !isCollapsed && (
          <div className="mt-1 space-y-0.5 pl-2 border-l border-[var(--glass-border-subtle)] ml-5">
            {visibleChildren.map((child) => renderSidebarItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={cn(
      'glass-sidebar transition-all duration-300 flex flex-col h-full',
      isCollapsed ? 'w-16' : 'w-64',
      className
    )}>
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-4 border-b border-[var(--glass-border-subtle)]">
        {!isCollapsed && (
          <span
            className="text-[10px] uppercase tracking-widest font-semibold"
            style={{ color: 'var(--glass-text-subtle)' }}
          >
            Navigation
          </span>
        )}
        <button
          onClick={onToggle}
          className="p-2 rounded-lg hover:bg-white/10 transition-all duration-200"
          style={{ color: 'var(--glass-text-muted)' }}
          title={isCollapsed ? 'Expand' : 'Collapse'}
        >
          <Menu className="w-4 h-4" />
        </button>
      </div>

      {/* Navigation Items */}
      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {sidebarItems.map(item => renderSidebarItem(item))}
      </div>

      {/* Footer */}
      {!isCollapsed && (
        <div className="flex-shrink-0 p-4 border-t border-[var(--glass-border-subtle)]">
          <div
            className="glass-subtle rounded-xl p-3 text-center"
          >
            <span className="text-[10px] uppercase tracking-wider font-medium" style={{ color: 'var(--glass-text-subtle)' }}>
              Portfolio v2.0
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
